import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Search, Mic, ArrowRight, Users } from "lucide-react";
import { getGuests } from "../lib/api";

// Fallback derived from mock episodes so the page never breaks.
import { episodes } from "../mock/mock";
function deriveGuests() {
  const map = {};
  episodes.forEach((ep) => {
    if (ep.guest === "The Kellys") return;
    if (!map[ep.guest]) map[ep.guest] = { name: ep.guest, role: ep.guestRole, bio: ep.guestBio, img: ep.guestImg, episodes: [] };
    map[ep.guest].episodes.push({ id: ep.id, number: ep.number, title: ep.title, pillar: ep.pillar });
  });
  return Object.values(map).sort((a, b) => a.name.localeCompare(b.name));
}

export default function Guests() {
  const [guests, setGuests] = useState(deriveGuests());
  const [query, setQuery] = useState("");

  useEffect(() => {
    getGuests()
      .then((data) => { if (Array.isArray(data) && data.length) setGuests(data); })
      .catch(() => {});
  }, []);

  const filtered = guests.filter(
    (g) => g.name.toLowerCase().includes(query.toLowerCase()) || g.role.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div>
      {/* Hero */}
      <section className="max-w-4xl mx-auto px-5 pt-16 pb-8 text-center">
        <p className="font-ui tracking-[0.3em] text-teal text-sm font-semibold uppercase flex items-center justify-center gap-2">
          <Users size={16} /> Guest Directory
        </p>
        <h1 className="font-serif-display text-5xl md:text-6xl font-bold text-navy mt-4">The people who got real.</h1>
        <p className="font-ui text-navy/70 mt-5 text-lg leading-relaxed max-w-2xl mx-auto">
          Every founder, operator, and expert who's sat down with the Kellys on Getting Real with BOSSY. Search by name
          or expertise — and if you'd love to be next, we'd love to have you.
        </p>
        <div className="relative max-w-md mx-auto mt-8">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-navy/40" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search guests by name or expertise…"
            className="w-full pl-11 pr-4 py-3.5 rounded-full bg-white border font-ui text-sm outline-none focus:ring-2 ring-teal"
            style={{ borderColor: "var(--cream-dark)" }}
          />
        </div>
      </section>

      {/* Grid */}
      <section className="max-w-6xl mx-auto px-5 py-12">
        {filtered.length === 0 ? (
          <p className="text-center font-ui text-navy/60 py-16">No guests match your search.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {filtered.map((g) => (
              <div key={g.name} className="lift bg-white rounded-2xl overflow-hidden border flex flex-col" style={{ borderColor: "var(--cream-dark)" }}>
                <img src={g.img} alt={g.name} className="w-full h-56 object-cover" />
                <div className="p-6 flex flex-col flex-1">
                  <h3 className="font-serif-display text-2xl font-bold text-navy">{g.name}</h3>
                  <p className="font-ui text-sm text-teal font-medium">{g.role}</p>
                  <p className="font-ui text-sm text-navy/70 mt-3 leading-relaxed flex-1">{g.bio}</p>
                  <div className="mt-5 pt-4 border-t" style={{ borderColor: "var(--cream-dark)" }}>
                    <p className="font-ui text-xs text-navy/50 uppercase tracking-wider mb-2 flex items-center gap-1.5"><Mic size={13} /> Appearances</p>
                    {g.episodes.map((ep) => (
                      <Link key={ep.id} to={`/podcast/${ep.id}`} className="flex items-center justify-between group py-1.5">
                        <span className="font-ui text-sm text-navy/80 group-hover:text-orange transition-colors truncate">EP {ep.number} · {ep.title}</span>
                        <ArrowRight size={14} className="text-teal shrink-0 ml-2 group-hover:translate-x-1 transition-transform" />
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Be a guest CTA */}
      <section className="max-w-4xl mx-auto px-5 pb-8">
        <div className="bg-navy text-cream rounded-2xl p-8 md:p-10 text-center">
          <h3 className="font-serif-display text-3xl font-bold">Want to be on the show?</h3>
          <p className="font-ui text-white/80 mt-3 max-w-xl mx-auto">We're always looking for business owners with a real story to tell — the messy middle, not just the highlight reel.</p>
          <Link to="/work-with-us" className="mt-6 inline-flex items-center gap-2 bg-teal text-navy px-7 py-3.5 rounded-full font-ui font-semibold hover:bg-cream transition-colors">
            Pitch Yourself as a Guest <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
